import { getMallList } from 'app/api/mall';
import { getLiabilityReport } from 'app/api/report';
import { startExportPoll, triggerDownload } from 'app/utils/exportPoller';
import Message from 'app/utils/Message';
import Toast from 'app/utils/Toast';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const useLiabilityReport = () => {
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const navigate = useNavigate();
  const [values, setValues] = useState({
    mallId: '',
    startDate: null,
    endDate: null,
  });
  const [totalNumber, setTotalNumber] = useState(0);
  const [filteredData, setFilteredData] = useState([]);
  const [pageLoading, setPageLoading] = useState(false);
  const [pageCount, setPageCount] = useState(1);
  const currentPage = 1;
  const dataPerPage = 10;
  const [mallList, setMallList] = useState([]);
  const downloadInProgressRef = useRef(false);
  const [downloadInProgress, setDownloadInProgress] = useState(false);

  const sectionObj = {
    sectionTitle: 'Liability Report',

    downloadObj: {
      excelClick: (e) => {
        handleDownloadexcel(e);
      },
      csvClick: (e) => {
        handleDownloadcsv(e);
      },

      pdfClick: () => () => {},
      inProgress: downloadInProgress,
    },
  };

  useEffect(() => {
    getMallListforDropdown();
  }, []);

  const getMallListforDropdown = async () => {
    setPageLoading(true);
    let response = await getMallList(1);
    setPageLoading(false);
    if (response && response?.responseCode === 200) {
      let temp = await response?.data?.malls?.map((item, index) => {
        return {
          ...item,
          value: item.id,
          label: item.name,
        };
      });
      temp.unshift({ value: 'All', label: 'All Mall' });
      setMallList([...temp]);
    } else {
      Toast.error(
        response?.data ? response?.data : Message?.Error?.COMMON_MESSAGE
      );
    }
  };

  const fetchReportData = useCallback(
    async (page, search = '', filters = {}) => {
      if (!filters.mallId || !filters.startDate || !filters.endDate) {
        return;
      }
      setPageLoading(true);
      try {
        let requestParams = {
          page,
          limit: dataPerPage,
          search,
          mallId: filters.mallId,
          startDate: filters.startDate,
          endDate: filters.endDate,
        };

        const response = await getLiabilityReport(requestParams);
        if (response?.responseCode === 200) {
          const dataArr = response?.data?.data || [];
          const newArray = dataArr.map((item) => ({
            ...item,
          }));
          setFilteredData(newArray);
          const totalCount = response?.data?.count || 0;
          const tempcount = Math.ceil(totalCount / dataPerPage);
          setTotalNumber(totalCount);
          setPageCount(tempcount);
        } else {
          Toast.error(
            response?.response?.message || Message.Error.COMMON_MESSAGE
          );
        }
      } catch (error) {
        Toast.error(Message.Error.COMMON_MESSAGE);
      } finally {
        setPageLoading(false);
      }
    },
    [dataPerPage]
  );

  function handleSubmit(event) {
    event.preventDefault();
    fetchReportData(1, '', values);
  }

  // Async export: returns a jobId, then polls /export/status/:jobId. We DON'T
  // block the UI with pageLoading — the Toast is enough feedback, and forcing
  // a full-page loader prevents the user from working while the export runs
  // (which can take minutes for large datasets).
  async function startDownload(downloadType) {
    if (downloadInProgressRef.current) return;
    downloadInProgressRef.current = true;
    setDownloadInProgress(true);
    let filters = values;
    let requestParams = {
      page: 1,
      limit: totalNumber,
      mallId: filters.mallId,
      startDate: filters.startDate,
      endDate: filters.endDate,
      download: true,
      downloadType,
    };

    try {
      const response = await getLiabilityReport(requestParams);
      const jobId = response?.data?.jobId;
      if (response?.responseCode !== 200 || !jobId) {
        Toast.error(response?.response?.message || Message.Error.COMMON_MESSAGE);
        downloadInProgressRef.current = false;
        setDownloadInProgress(false);
        return;
      }

      Toast.success('Report generation started');

      startExportPoll(jobId, {
        onDone: (url) => {
          triggerDownload(url);
          downloadInProgressRef.current = false;
          setDownloadInProgress(false);
        },
        onFailed: (error) => {
          Toast.error(error || Message.Error.COMMON_MESSAGE);
          downloadInProgressRef.current = false;
          setDownloadInProgress(false);
        },
        onError: () => {
          Toast.error(Message.Error.COMMON_MESSAGE);
          downloadInProgressRef.current = false;
          setDownloadInProgress(false);
        },
      });
    } catch (error) {
      console.error('startDownload failed:', error);
      Toast.error(Message.Error.COMMON_MESSAGE);
      downloadInProgressRef.current = false;
      setDownloadInProgress(false);
    }
  }

  async function handleDownloadcsv() {
    return startDownload('csv');
  }

  async function handleDownloadexcel() {
    return startDownload('excel');
  }
  function setChange(e) {
    const { name, value } = e.target;
    setValues((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  }
  function setChangegetvalue(e) {
    const { name, value } = e.target;
    setValues((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  }

  return {
    pageLoading,
    filteredData,
    fetchReportData,
    navigate,
    totalNumber,
    currentPage,
    dataPerPage,
    pageCount,
    sectionObj,
    isFilterOpen,
    setIsFilterOpen,
    values,
    setValues,
    handleSubmit,
    mallList,
    setChange,
    setChangegetvalue,
  };
};

export default useLiabilityReport;
